/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  FormControl,
  IconButton,
  InputAdornment,
  Select,
  Typography,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { NavigableBreadcrumbLink } from "../../../components/page_breadcrumbs/navigable_breadcrumb_link";
import api from "../../../api";
import authenticatedFetch from "../../../api/authenticatedFetch";
import { buildQueryParams } from "../../../utils/buildQueryParams";
import DropdownSearchBar from "../../../components/dorpdown_searchbar";
import { DropdownInfiniteLoader } from "../../../components/dropdown_infinite_loader";
import { IndexTable } from "../../../components/index_table";
import { Loader } from "../../../components/loader";
import { MenuHeading } from "../../../components/menu_heading";
import { ResetButton } from "../../../components/reset_button";
import { SelectedMenuItem } from "../../../components/selected_menu_item";
import { StyledMenuItem } from "../../../components/styled_menu_item";
import { UpdateButton } from "../../../components/update_button";
import {
  SavedQueryChip,
  SavedQueryControls,
  seedOptionsCache,
  useSavedQueryState,
} from "../../../components/saved_queries";
import { Query_Keys } from "../../../lib/queryKeys";
import { useNotificationContext } from "../../../contexts/notificationContext";
import { PerPage } from "../../../constants/perPage";
import { useDebounce } from "../../../utils/useDebounce";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import {
  getDropdownNextPageParam,
  mergeOptionsIfChanged,
  mergeUniqueByKey,
  normalizeRestaurant,
  normalizeRestaurantGroup,
} from "../../../utils/dropdownInfiniteQuery";
import { useInfiniteScrollObserver } from "../../../hooks/useInfiniteScrollObserver";
import { ALL_FILTER_VALUE, formatMoney } from "../../../utils/perqReportDrilldown";
import {
  AMOUNT_COLUMNS,
  DATE_FILTERS,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  ALL_ORDER_TYPE,
  ORDER_TYPES,
  ORDER_TYPE_LABELS,
  ORDER_TYPE_PLACEHOLDER,
  NAME_COLUMN,
  SUBTOTAL_TYPES,
  defaultBillingReportFilters,
  getBillingReportApiFilters,
  getBillingReportFilterError,
} from "./constants";
import { SubtotalOrdersModal } from "./subtotal_orders_modal";
import { billingSavedQueryAdapter } from "./savedQueryAdapter";
import { StyledSubtotalLink } from "./styles";
import {
  StyledBreadCrumbContainer,
  StyledLink,
  filterContainerStyles,
  selectBaseStyles,
  viewTypographyStyles,
  datePickerStyles,
  dropdownMenuProps,
  plainMenuProps,
} from "../perq_report/styles";

// Same grey as IndexTable's striped rows.
const GROUP_ROW_BACKGROUND = "#f7f7f7";

// Names a filter select for screen readers without adding visible text; the select's value stays its placeholder.
const HIDDEN_LABEL_SX = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: "-1px",
  padding: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const HiddenLabel = ({ id, children }) => (
  <Box component="span" id={id} sx={HIDDEN_LABEL_SX}>
    {children}
  </Box>
);

const normalizeDayjsValue = (value) => {
  if (!value) return null;
  if (typeof value?.isValid === "function" && typeof value?.format === "function") {
    return value;
  }
  const parsedValue = dayjs(value?.$d || value);
  return parsedValue.isValid() ? parsedValue : null;
};

const COLUMNS = [
  { ...NAME_COLUMN, width: 240 },
  ...AMOUNT_COLUMNS.map(({ key, label, forgone, description }) => ({
    key,
    label,
    width: forgone ? 220 : 190,
    show_info: true,
    description,
  })),
];

// Replaces the table's odd-row striping: group rows are always grey and bold, restaurant rows always white.
export const getBillingReportRowProps = (row) =>
  row.isGroup
    ? {
        "data-row-type": "group",
        sx: {
          "&, &:nth-of-type(odd)": { backgroundColor: GROUP_ROW_BACKGROUND },
          "& td": { fontWeight: 700 },
        },
      }
    : {
        "data-row-type": "restaurant",
        sx: { "&, &:nth-of-type(odd)": { backgroundColor: "#ffffff" } },
      };

// Non-zero subtotals open their orders; fees and zero subtotals stay plain text.
const toAmountCells = (amounts, row, onSubtotalClick) =>
  AMOUNT_COLUMNS.reduce((cells, { key, label }) => {
    const text = formatMoney(amounts?.[key]);
    const subtotalType = SUBTOTAL_TYPES[key];
    if (!subtotalType || !Number(amounts?.[key])) return { ...cells, [key]: text };

    const display = (
      <StyledSubtotalLink
        type="button"
        aria-label={`${label}, ${row.name}: ${text}. Show the orders`}
        data-testid={`billing-report-subtotal-${row.isGroup ? "group" : "restaurant"}-${row.id}-${subtotalType}`}
        onClick={() => onSubtotalClick({ ...row, subtotalType, label })}
      >
        {text}
      </StyledSubtotalLink>
    );
    return { ...cells, [key]: { display, sortValue: amounts?.[key] } };
  }, {});

export const BillingReport = () => {
  const showNotification = useNotificationContext();

  const [filters, setFilters] = useState(defaultBillingReportFilters());
  const [appliedFilters, setAppliedFilters] = useState(defaultBillingReportFilters());
  const [filtersChanged, setFiltersChanged] = useState(false);
  const savedQuery = useSavedQueryState();

  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("");
  const [appliedPerqSearch, setAppliedPerqSearch] = useState("");
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([]);
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([]);

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [csvLoading, setCsvLoading] = useState(false);
  const [subtotalSelection, setSubtotalSelection] = useState(null);

  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);
  const debouncedAppliedPerqSearch = useDebounce(appliedPerqSearch.trim(), 300);

  const hasGroupSelected = filters.restaurant_group_id !== ALL_FILTER_VALUE;
  const hasRestaurantsSelected = !filters.restaurant_ids.includes(ALL_FILTER_VALUE);
  const hasAppliedPerqsSelected = filters.applied_discount_names.length > 0;
  const hasOrderTypeSelected = filters.order_type !== ALL_ORDER_TYPE;

  const apiFilters = useMemo(() => getBillingReportApiFilters(appliedFilters), [appliedFilters]);
  // The endpoint rejects requests without a date range; no applied perq means every managed one.
  const canFetchReport = Boolean(apiFilters.start_date && apiFilters.end_date);

  const fetchRestaurantGroups = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurant_groups_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    });
    return response?.data?.map(normalizeRestaurantGroup) || [];
  };

  const {
    data: restaurantGroups = [],
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupsPage,
    hasNextPage: hasNextRestaurantGroupsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({
      search: debouncedRestaurantGroupSearch,
      source: "billing_report",
    }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantGroupsPage,
      hasNextPage: hasNextRestaurantGroupsPage,
      isFetchingNextPage: restaurantGroupsFetchingNextPage,
    });

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/restaurants_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
        restaurant_group_ids: hasGroupSelected ? filters.restaurant_group_id : undefined,
      },
    });
    return (
      response?.data?.map((record) => ({
        ...normalizeRestaurant(record),
        id: Number(record?.value ?? record?.id),
      })) || []
    );
  };

  const {
    data: restaurants = [],
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: filters.restaurant_group_id,
      search: debouncedRestaurantSearch,
      source: "billing_report",
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextRestaurantsPage,
      hasNextPage: hasNextRestaurantsPage,
      isFetchingNextPage: restaurantsFetchingNextPage,
    });

  // Deactivated perqs still bill, so the dropdown must list inactive names too.
  const fetchAppliedPerqs = async ({ pageParam = 1 }) => {
    const response = await api.get("/v1/admin/dropdowns/managed_configured_discount_list", {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedAppliedPerqSearch || undefined,
        restaurant_group_ids: hasGroupSelected ? filters.restaurant_group_id : undefined,
        restaurant_ids: hasRestaurantsSelected ? filters.restaurant_ids : undefined,
        include_inactive: true,
      },
    });
    return response?.data || [];
  };

  const {
    data: appliedPerqs = [],
    isFetching: appliedPerqsFetching,
    isFetchingNextPage: appliedPerqsFetchingNextPage,
    fetchNextPage: fetchNextAppliedPerqsPage,
    hasNextPage: hasNextAppliedPerqsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.managed_configured_discounts_dropdown({
      restaurant_group_id: filters.restaurant_group_id,
      restaurant_ids: filters.restaurant_ids,
      search: debouncedAppliedPerqSearch,
      include_inactive: true,
    }),
    queryFn: fetchAppliedPerqs,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const { rootRef: appliedPerqMenuRef, targetRef: appliedPerqLoaderRef } =
    useInfiniteScrollObserver({
      fetchNextPage: fetchNextAppliedPerqsPage,
      hasNextPage: hasNextAppliedPerqsPage,
      isFetchingNextPage: appliedPerqsFetchingNextPage,
    });

  // The endpoint pages restaurant groups; each group arrives with all its restaurants.
  const fetchReport = async () => {
    const response = await api.get("/v1/admin/transaction_fee_reports", {
      params: {
        page,
        per_page: itemsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...apiFilters,
      },
    });

    return {
      data: response?.data?.restaurant_groups || [],
      pageCount: Number(response?.headers?.["total-count"] || 0),
    };
  };

  const { data: report, isFetching, error, refetch } = useQuery({
    queryKey: Query_Keys.billing_report({
      queryFilter: apiFilters,
      page,
      itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchReport,
    enabled: canFetchReport,
    // Billed figures: a page, sort or filter off screen is dropped, so returning to it always reloads.
    // staleTime still stops a reload behind the loader each time the tab regains focus.
    gcTime: 0,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setRestaurantGroupOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurantGroups, "label", ["value"]),
    );
  }, [restaurantGroups]);

  useEffect(() => {
    setRestaurantOptionsCache((current) =>
      mergeOptionsIfChanged(current, restaurants, "id", ["name"]),
    );
  }, [restaurants]);

  useEffect(() => {
    if (!error) return;
    const message = error?.response?.data?.error || "Failed to load billing report.";
    showNotification(message, "error");
  }, [error, showNotification]);

  const selectedRestaurantGroupOption = restaurantGroupOptionsCache?.find(
    (group) => `${group?.label}` === `${filters.restaurant_group_id}`,
  );
  const displayedRestaurantGroups = restaurantGroupSearch.trim()
    ? restaurantGroups
    : selectedRestaurantGroupOption
      ? mergeUniqueByKey(restaurantGroups, [selectedRestaurantGroupOption], "label")
      : restaurantGroups;

  const selectedRestaurantOptions = restaurantOptionsCache?.filter((restaurant) =>
    filters.restaurant_ids?.some((id) => `${id}` === `${restaurant?.id}`),
  );
  const displayedRestaurants = restaurantSearch.trim()
    ? restaurants
    : mergeUniqueByKey(restaurants, selectedRestaurantOptions, "id");

  // Without a search term, keep selected perqs listed even when the current scope no longer returns them.
  const displayedAppliedPerqs = appliedPerqSearch.trim()
    ? appliedPerqs
    : mergeUniqueByKey(
        appliedPerqs,
        filters.applied_discount_names.map((name) => ({ label: name, value: name })),
        "value",
      );

  const getRestaurantGroupName = (id) => {
    if (id === ALL_FILTER_VALUE) return "All restaurant groups";
    const cached = restaurantGroupOptionsCache?.find((group) => `${group?.label}` === `${id}`);
    return cached?.value || "Restaurant groups";
  };

  const getRestaurantDisplayValue = () => {
    if (!hasRestaurantsSelected) return "All restaurants";
    if (filters.restaurant_ids.length === 1) {
      const restaurant = restaurantOptionsCache?.find(
        (record) => `${record.id}` === `${filters.restaurant_ids[0]}`,
      );
      return restaurant?.name || "Restaurants";
    }
    return `${filters.restaurant_ids.length} Restaurants Selected`;
  };

  const getAppliedPerqsDisplayValue = () => {
    if (!hasAppliedPerqsSelected) return "All managed applied perqs";
    if (filters.applied_discount_names.length === 1) return filters.applied_discount_names[0];
    return `${filters.applied_discount_names.length} Perqs Selected`;
  };

  const markChanged = () => setFiltersChanged(true);

  const handleDateFilterChange = (event) => {
    const nextDateFilter = event.target.value;
    setFilters((prev) => ({
      ...prev,
      dateFilter: nextDateFilter,
      customDateFrom: nextDateFilter === "Custom Date" ? null : prev.customDateFrom,
      customDateTo: nextDateFilter === "Custom Date" ? null : prev.customDateTo,
    }));
    markChanged();
  };

  const handleDateChange = (field, value) => {
    setFilters((prev) => {
      if (field === "customDateFrom") {
        if (!value) return { ...prev, customDateFrom: null, customDateTo: null };

        return {
          ...prev,
          customDateFrom: value,
          customDateTo:
            isValidDayjsValue(value) && !isValidDayjsValue(prev.customDateTo)
              ? dayjs().startOf("day")
              : prev.customDateTo,
        };
      }

      return { ...prev, [field]: normalizeDayjsValue(value) };
    });
    markChanged();
  };

  const resetGroupScopedFilters = (restaurantGroupId) => {
    setRestaurantSearch("");
    setAppliedPerqSearch("");
    setFilters((prev) => ({
      ...prev,
      restaurant_group_id: restaurantGroupId,
      restaurant_ids: [ALL_FILTER_VALUE],
      applied_discount_names: [],
    }));
    markChanged();
  };

  const handleRestaurantGroupChange = (event) => resetGroupScopedFilters(event.target.value);
  const handleRestaurantGroupClear = () => resetGroupScopedFilters(ALL_FILTER_VALUE);

  const handleRestaurantChange = (event) => {
    const { value } = event.target;
    if ((value.length === 1 && value.includes(ALL_FILTER_VALUE)) || value.length === 0) {
      setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE] }));
    } else {
      setFilters((prev) => ({
        ...prev,
        restaurant_ids: value.filter((record) => record !== ALL_FILTER_VALUE),
      }));
    }
    markChanged();
  };

  const handleRestaurantClear = () => {
    setFilters((prev) => ({ ...prev, restaurant_ids: [ALL_FILTER_VALUE] }));
    markChanged();
  };

  const handleOrderTypeChange = (event) => {
    setFilters((prev) => ({ ...prev, order_type: event.target.value }));
    markChanged();
  };

  const handleOrderTypeClear = () => {
    setFilters((prev) => ({ ...prev, order_type: ALL_ORDER_TYPE }));
    markChanged();
  };

  const handleAppliedPerqsChange = (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      applied_discount_names: (Array.isArray(value) ? value : []).filter(Boolean),
    }));
    markChanged();
  };

  const handleAppliedPerqsClear = () => {
    setFilters((prev) => ({ ...prev, applied_discount_names: [] }));
    markChanged();
  };

  const handleColumnSortChange = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setPage(1);
  };

  // Returns false when the filters are invalid, after telling the user why.
  const runReport = (nextFilters) => {
    const filterError = getBillingReportFilterError(nextFilters);
    if (filterError) {
      showNotification(filterError, "error");
      return false;
    }

    // Same filters on page 1 keep the query key, so React Query would not fetch; refetch so Update always reloads.
    const keepsQueryKey =
      page === 1 && JSON.stringify(getBillingReportApiFilters(nextFilters)) === JSON.stringify(apiFilters);

    // A new object, so a preset like Today resolves to the current date again (e.g. after midnight).
    setAppliedFilters({ ...nextFilters });
    setFiltersChanged(false);
    setPage(1);
    if (keepsQueryKey && canFetchReport) refetch();
    return true;
  };

  const handleApplyFilters = () => runReport(filters);

  const savedQueryLookups = {
    restaurantGroupName: (id) =>
      restaurantGroupOptionsCache.find((group) => `${group?.label}` === `${id}`)?.value,
    restaurantName: (id) =>
      restaurantOptionsCache.find((restaurant) => `${restaurant?.id}` === `${id}`)?.name,
  };

  // Fills the filters and runs the report; invalid filters stay filled for the user to fix, and a query with removed filters waits for Update.
  const handleApplySavedQuery = (restoredFilters, labelOptions, { restricted = false } = {}) => {
    const { restaurant_group: group, restaurants: savedRestaurants } = labelOptions;
    if (group) {
      setRestaurantGroupOptionsCache((current) =>
        seedOptionsCache(current, [{ label: group.id, value: group.label }], "label"),
      );
    }
    setRestaurantOptionsCache((current) =>
      seedOptionsCache(current, savedRestaurants.map(({ id, label }) => ({ id, name: label })), "id"),
    );
    setRestaurantGroupSearch("");
    setRestaurantSearch("");
    setAppliedPerqSearch("");
    setFilters(restoredFilters);
    if (restricted || !runReport(restoredFilters)) setFiltersChanged(true);
  };

  const handleReset = () => {
    savedQuery.clearLoadedQuery();
    const resetFilters = defaultBillingReportFilters();
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setFiltersChanged(false);
    setPage(1);
    setItemsPerPage(25);
    setSortBy(DEFAULT_SORT_BY);
    setSortOrder(DEFAULT_SORT_ORDER);
    setRestaurantGroupSearch("");
    setRestaurantSearch("");
    setAppliedPerqSearch("");
  };

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);

      const query = buildQueryParams({
        report: "transaction_fee_report",
        sort_by: sortBy,
        sort_order: sortOrder,
        ...apiFilters,
      });

      const { response } = await authenticatedFetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?${query}`,
        {
          method: "GET",
          defaultErrorMessage: "Failed to download CSV",
        },
      );

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = `billing_report_${dayjs().format("YYYY-MM-DD")}.csv`;
      link.click();

      window.URL.revokeObjectURL(downloadUrl);
      showNotification("CSV downloaded successfully", "success");
    } catch (exportError) {
      showNotification(exportError?.message || "Failed to download CSV", "error");
    } finally {
      setCsvLoading(false);
    }
  };

  const handleSubtotalClick = useCallback((selection) => setSubtotalSelection(selection), []);

  // Each group row is followed by its restaurants, in the order the backend sorted them.
  const rows = useMemo(
    () =>
      (report?.data || []).flatMap((group) => [
        {
          id: `group-${group.id}`,
          isGroup: true,
          name: group.name,
          ...toAmountCells(group.totals, { isGroup: true, id: group.id, name: group.name }, handleSubtotalClick),
        },
        ...(group.restaurants || []).map((restaurant) => ({
          id: `restaurant-${restaurant.id}`,
          isGroup: false,
          name: restaurant.name,
          ...toAmountCells(
            restaurant,
            { isGroup: false, id: restaurant.id, name: restaurant.name },
            handleSubtotalClick,
          ),
        })),
      ]),
    [report?.data, handleSubtotalClick],
  );

  return (
    <Box sx={{ height: "100%", width: "100%", overflowY: "scroll", overflowX: "hidden" }}>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
          <StyledLink color="#515152">Reports</StyledLink>
          <StyledLink color="#515152">OwnersTable Reports</StyledLink>
          <StyledLink color="#515152">Billing Report</StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <Box sx={filterContainerStyles}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", lg: "center" },
            gap: { xs: 2, lg: 1 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: "12px" },
              flexWrap: "wrap",
              alignItems: { xs: "flex-start", sm: "center" },
              flex: 1,
            }}
          >
            <Typography variant="body2" sx={viewTypographyStyles}>
              View
            </Typography>

            <SavedQueryChip adapter={billingSavedQueryAdapter} filters={filters} state={savedQuery} />

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "150px" } }}>
              <HiddenLabel id="billing-report-date-filter-label">Date range</HiddenLabel>
              <Select
                labelId="billing-report-date-filter-label"
                data-testid="billing-report-date-filter"
                value={filters.dateFilter}
                renderValue={() => filters.dateFilter}
                onChange={handleDateFilterChange}
                sx={selectBaseStyles}
                MenuProps={plainMenuProps}
              >
                {DATE_FILTERS.map((value) => (
                  <StyledMenuItem key={value} value={value}>
                    {filters.dateFilter === value ? <SelectedMenuItem data={value} /> : value}
                  </StyledMenuItem>
                ))}
              </Select>
            </FormControl>

            {filters.dateFilter === "Custom Date" ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Typography variant="body2" sx={viewTypographyStyles}>
                  from
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="MM-DD-YYYY"
                    value={normalizeDayjsValue(filters.customDateFrom)}
                    onChange={(value) => handleDateChange("customDateFrom", value)}
                    slots={{ openPickerIcon: CalendarMonthIcon }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { ...datePickerStyles, minWidth: 140 },
                        inputProps: { "data-testid": "billing-report-date-from", "aria-label": "Date range from" },
                      },
                    }}
                  />
                </LocalizationProvider>
                <Typography variant="body2" sx={viewTypographyStyles}>
                  through
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    format="MM-DD-YYYY"
                    value={normalizeDayjsValue(filters.customDateTo)}
                    onChange={(value) => handleDateChange("customDateTo", value)}
                    slots={{ openPickerIcon: CalendarMonthIcon }}
                    slotProps={{
                      textField: {
                        size: "small",
                        sx: { ...datePickerStyles, minWidth: 140 },
                        inputProps: { "data-testid": "billing-report-date-to", "aria-label": "Date range through" },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
            ) : null}

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "185px" } }}>
              <HiddenLabel id="billing-report-restaurant-group-label">Restaurant group</HiddenLabel>
              <Select
                labelId="billing-report-restaurant-group-label"
                data-testid="billing-report-restaurant-group"
                value={hasGroupSelected ? filters.restaurant_group_id : ""}
                displayEmpty
                renderValue={() => getRestaurantGroupName(filters.restaurant_group_id)}
                onChange={handleRestaurantGroupChange}
                onClose={() => setTimeout(() => setRestaurantGroupSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  hasGroupSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        aria-label="Clear restaurant group"
                        onClick={handleRestaurantGroupClear}
                      >
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(restaurantGroupMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setRestaurantGroupSearch}
                  placeholder="Search restaurant group"
                  isFetching={restaurantGroupFetching}
                />
                {displayedRestaurantGroups.map((group) => (
                  <StyledMenuItem key={group.label} value={group.label}>
                    {`${filters.restaurant_group_id}` === `${group.label}` ? (
                      <SelectedMenuItem data={group.value} />
                    ) : (
                      group.value
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantGroupsPage}
                  targetRef={restaurantGroupLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "195px" } }}>
              <HiddenLabel id="billing-report-restaurant-label">Restaurants</HiddenLabel>
              <Select
                labelId="billing-report-restaurant-label"
                data-testid="billing-report-restaurant"
                multiple
                value={hasRestaurantsSelected ? filters.restaurant_ids : []}
                displayEmpty
                renderValue={getRestaurantDisplayValue}
                onChange={handleRestaurantChange}
                onClose={() => setTimeout(() => setRestaurantSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  hasRestaurantsSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        aria-label="Clear restaurants"
                        onClick={handleRestaurantClear}
                      >
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(restaurantMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setRestaurantSearch}
                  placeholder="Search restaurant"
                  isFetching={restaurantFetching}
                />
                {displayedRestaurants.map((restaurant) => (
                  <StyledMenuItem key={restaurant.id} value={restaurant.id}>
                    {filters.restaurant_ids.includes(restaurant.id) ? (
                      <SelectedMenuItem data={restaurant.name} />
                    ) : (
                      restaurant.name
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextRestaurantsPage}
                  targetRef={restaurantLoaderRef}
                />
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "150px" } }}>
              <HiddenLabel id="billing-report-order-type-label">Orders</HiddenLabel>
              <Select
                labelId="billing-report-order-type-label"
                data-testid="billing-report-order-type"
                value={hasOrderTypeSelected ? filters.order_type : ""}
                displayEmpty
                renderValue={() =>
                  hasOrderTypeSelected ? ORDER_TYPE_LABELS[filters.order_type] : ORDER_TYPE_PLACEHOLDER
                }
                onChange={handleOrderTypeChange}
                sx={selectBaseStyles}
                endAdornment={
                  hasOrderTypeSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        aria-label="Clear orders filter"
                        onClick={handleOrderTypeClear}
                      >
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={plainMenuProps}
              >
                {ORDER_TYPES.map((value) => (
                  <StyledMenuItem key={value} value={value}>
                    {filters.order_type === value ? (
                      <SelectedMenuItem data={ORDER_TYPE_LABELS[value]} />
                    ) : (
                      ORDER_TYPE_LABELS[value]
                    )}
                  </StyledMenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: "185px" } }}>
              <HiddenLabel id="billing-report-applied-perqs-label">Managed applied perqs</HiddenLabel>
              <Select
                labelId="billing-report-applied-perqs-label"
                data-testid="billing-report-applied-perqs"
                multiple
                displayEmpty
                value={filters.applied_discount_names}
                renderValue={getAppliedPerqsDisplayValue}
                onChange={handleAppliedPerqsChange}
                onClose={() => setTimeout(() => setAppliedPerqSearch(""), 250)}
                sx={selectBaseStyles}
                endAdornment={
                  hasAppliedPerqsSelected ? (
                    <InputAdornment position="end" sx={{ position: "absolute", right: 25 }}>
                      <IconButton
                        edge="end"
                        size="small"
                        disableRipple
                        aria-label="Clear managed applied perqs"
                        onClick={handleAppliedPerqsClear}
                      >
                        <ClearIcon sx={{ fontSize: "20px" }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }
                MenuProps={dropdownMenuProps(appliedPerqMenuRef)}
              >
                <DropdownSearchBar
                  changeSearchText={setAppliedPerqSearch}
                  placeholder="Search managed applied perq"
                  isFetching={appliedPerqsFetching}
                />
                {displayedAppliedPerqs.map((perq) => (
                  <StyledMenuItem key={perq.value} value={perq.value}>
                    {filters.applied_discount_names.includes(perq.value) ? (
                      <SelectedMenuItem data={perq.label} />
                    ) : (
                      perq.label
                    )}
                  </StyledMenuItem>
                ))}
                <DropdownInfiniteLoader
                  hasNextPage={hasNextAppliedPerqsPage}
                  targetRef={appliedPerqLoaderRef}
                />
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              flexWrap: "wrap",
              width: { xs: "100%", lg: "auto" },
            }}
          >
            <SavedQueryControls
              reportType="billing_report"
              filters={filters}
              appliedFilters={appliedFilters}
              lookups={savedQueryLookups}
              adapter={billingSavedQueryAdapter}
              state={savedQuery}
              onApply={handleApplySavedQuery}
              isDataLoaded={canFetchReport && !isFetching && report !== undefined}
            />
            <ResetButton onClick={handleReset} />
            <UpdateButton
              onClick={handleApplyFilters}
              filtersChanged={filtersChanged}
              status={isFetching}
            />
          </Box>
        </Box>
      </Box>

      <MenuHeading report={false}>Billing Report</MenuHeading>

      <Box sx={{ pb: 2 }}>
        {isFetching ? (
          <Box
            data-testid="billing-report-table-loader"
            role="status"
            aria-live="polite"
            aria-label="Loading report results"
          >
            <Loader />
          </Box>
        ) : (
          <IndexTable
            report={false}
            columns={COLUMNS}
            data={rows}
            type="restaurant groups"
            tableId="billing-report"
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={report?.pageCount || 0}
            sortMode="server"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleColumnSortChange}
            getRowProps={getBillingReportRowProps}
            showDownloadButton={true}
            disableDownloadButton={rows.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
          />
        )}
      </Box>

      {subtotalSelection ? (
        <SubtotalOrdersModal
          selection={subtotalSelection}
          apiFilters={apiFilters}
          onClose={() => setSubtotalSelection(null)}
        />
      ) : null}
    </Box>
  );
};
